// client/components/Kunden/KundenBlockPendenzen.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../constants/api";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import ZuweisungBadge from "../ui/ZuweisungBadge.jsx";
import AppButton from "../ui/AppButton.jsx";

/* helpers */
const toTime = v => {
  if (!v) return 0;
  const s = /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00` : v;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
};
const fmtDate = v => v ? new Date(toTime(v)).toLocaleDateString("de-CH") : "—";
const D = 24 * 60 * 60 * 1000;
const relDue = v => {
  const t = toTime(v);
  if (!t) return "—";
  const diff = Math.round((t - Date.now()) / D);
  if (diff === 0) return "Heute";
  if (diff === 1) return "Morgen";
  if (diff > 1) return `in ${diff} Tagen`;
  if (diff === -1) return "Gestern fällig";
  return `${Math.abs(diff)} Tage überfällig`;
};

// Projekt-Typ Emojis
const PROJEKT_EMOJI = {
  elektro: "⚡",
  pv: "☀️",
  ladestation: "🔌",
  sicherheit: "🔐",
  ladenbau: "🏪",
  intern: "🏠"
};
export default function KundenBlockPendenzen({
  kundenId,
  onEmpty
}) {
    const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const navigate = useNavigate();
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort?.();
    };
  }, []);

  // Lädt Pendenzen des Kunden UND seiner Projekte
  const load = useCallback(async () => {
    if (!kundenId) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (mountedRef.current) setLoading(true);
      // Neuer Endpoint: lädt Kunde + Projekt-Pendenzen
      const {
        data
      } = await api.get(`/pendenzen/kunde/${kundenId}`, {
        params: {
          status: "offen"
        },
        signal: controller.signal
      });
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const enriched = list.map(it => ({
        ...it,
        _ts: toTime(it.aktualisiert_am || it.updated_at || it.created_at || it.erzeugt_am || it.faellig_am)
      })).sort((a, b) => (b._ts || 0) - (a._ts || 0));
      if (!mountedRef.current || abortRef.current !== controller) return;
      setItems(enriched);
      if (enriched.length === 0) onEmpty?.();
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
        if (!mountedRef.current || abortRef.current !== controller) return;
        setItems([]);
        onEmpty?.();
      }
    } finally {
      if (mountedRef.current && abortRef.current === controller) setLoading(false);
    }
  }, [kundenId, onEmpty]);
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [kundenId]);
  const overdueCount = useMemo(() => items.filter(it => toTime(it.faellig_am) && toTime(it.faellig_am) < Date.now()).length, [items]);
  const headerRight = useMemo(() => {
    const n = items.length;
    return `${n} offen${overdueCount ? ` · ${overdueCount} überfällig` : ""}`;
  }, [items.length, overdueCount]);
  const panelId = "kunden-pendenzen-panel";
  if (loading) {
    return (
      <section className="rounded-2xl border border-default bg-surface app-frame p-4 animate-pulse">
        <div className="h-5 w-48 rounded bg-white/10 mb-3" />
        <div className="h-12 rounded-2xl bg-white/10" />
      </section>
    );
  }
  if (!items.length) return null;
  const openItem = it => {
    navigate(`/meine-aufgaben?tab=pendenzen&open=${it.id}`);
  };
  const statusPill = s => s === "offen" ? "border-amber-400/30 bg-amber-400/10 text-amber-200" : s === "erledigt" ? "border-green-400/30 bg-green-400/10 text-green-200" : "border-white/15 bg-white/10 text-blue-200";
  return (
    <CollapsibleBlock
      title="📝 Pendenzen"
      headerInfo={headerRight}
      panelId={panelId}
      contentClassName="px-4 sm:px-6 pb-4 sm:pb-5"
    >
      <div className="space-y-2">
            {items.map(it => {
          const dueStr = fmtDate(it.faellig_am);
          const rel = relDue(it.faellig_am);
          const overdue = toTime(it.faellig_am) && toTime(it.faellig_am) < Date.now();
          const btnCls =
            "group interactive-card w-full !justify-start min-h-0 rounded-2xl px-3 py-2.5 border border-default bg-surface hover:!bg-surface transition-all";
          const projektEmoji = it.projekt_typ ? PROJEKT_EMOJI[it.projekt_typ] || "📋" : null;
          const projektLabel = it.projekt_name || (it.projekt_id ? `Projekt #${it.projekt_id}` : null);
          return <AppButton key={it.id} type="button" variant="ghost" onClick={() => openItem(it)} title="Pendenz öffnen" className={`${btnCls} !justify-start min-h-0`}>
                  <div className="grid grid-cols-[1fr_auto] items-start gap-3">
                    <div className="min-w-0">
                      <div className={`font-semibold truncate ${"text-blue-100"}`}>{it.titel || `Pendenz #${it.id}`}</div>
                    </div>
                    <div className={`text-xs whitespace-nowrap ${overdue ? "text-red-500" : "text-blue-200/70"}`}>Fällig: {dueStr} · {rel}</div>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                    {it.status && <span className={`px-2 py-0.5 rounded-md border ${statusPill(it.status)}`}>{it.status}</span>}
                    {projektLabel && <span className={"px-2 py-0.5 rounded-md border border-purple-400/30 bg-purple-400/10 text-purple-200"}>
                        {projektEmoji} {projektLabel}
                      </span>}
                    <ZuweisungBadge name={it.assignee_name || it.verantwortlich_name || "—"} variant="full" size="xs" truncateAt={18} title={it.assignee_name || it.verantwortlich_name || "Zuständig"} />
                  </div>
                  {it.beschreibung && <div className={`mt-1 text-xs line-clamp-2 ${"text-blue-100/90"}`}>{it.beschreibung}</div>}
                </AppButton>;
        })}
      </div>
    </CollapsibleBlock>
  );
}